/*
 * r16m (2026-10-20): the follow-back attribution views - pure transforms of the server's answer (design
 * D:/NecroWork/r16m-batch-evidence/DESIGN.md 3.4 / 3.6 / 3.7). Every rate is computed HERE from the server's counts: a batch rate is a
 * sum over a sum, never an average of slot rates; a change is in percentage points.
 *
 * The answer (one shape for a slot and for a group; the server owns it, this file reads it tolerantly):
 *   { scope:{kind, id, label, slots:[{slot, device, trackingSince, baselineNames}], tz, batch?}, asOf,
 *     window:{days:7}, cohort:{from, to}, maturing:{from, to, engaged, followedBack}, minRankN:30,
 *     signal:{slotsReadOk24h, slotsTotal, lastReadAt, methods:{OWNLIST, NOTIF}, note},
 *     sourceTypes:[{key, label}],
 *     totals:{engaged, followedBack, ambiguous, late, preFollowersExcluded, beforeTracking, unknownOutcome},
 *     previous:{cohort:{from, to}, engaged, followedBack},
 *     bySourceType:[{key, engaged, followedBack, maturing:{engaged, followedBack}}],
 *     byTarget:[{target, sourceType, engaged, followedBack, lastEngagedAt}],
 *     series:[{bucket, granularity, maturing, bySource:{KEY:{engaged, followedBack}}}],
 *     bySlot:[{slot, device, engaged, followedBack, bySource, signalOk, trackingSince, previous?}] }
 * The r16m server's review fixes (NSB-r16m-batch c0c82bd, 2026-10-20) add `engagedChecked` to EVERY set of counts: coverage is per
 * slot on the server, `engaged` counts everyone (the people-engaged figures and bars), `engagedChecked` only the people on slots
 * whose follow-back reads cover the period, and the rate is followedBack / engagedChecked (both null when no slot covers it). Here
 * every set of counts carries `checked` (= engagedChecked; for a server without it: engaged when followedBack is known, else null)
 * and every rate divides by it.
 * The design lists `maturing` twice (the range and the counts); a JSON object keeps one key, so both are read from the one object
 * (and from `maturingCounts` / `maturingRange` when a server splits them). The r16m server (NSB-r16m-batch FollowBackUiController /
 * FollowBackAggregation, read 2026-10-20) also sends: the slot's batch at the TOP level ({id, label, members: <count>}), scope.tz as
 * "+05:30" with scope.tzOffsetMinutes, signal.notReporting (slot names), followedBack null where no read covers the counts, the echo
 * range / target; all are read here.
 *
 * Times: every server time is epoch ms UTC (design 4.4); an ISO string is read too. Buckets are days ('YYYY-MM-DD', or epoch ms of the
 * bucket start) for 7 / 30 days and ISO weeks ('YYYY-Www', or the Monday) for All; labels use the scope's UTC offset (`tz`).
 *
 * The rules this view keeps (design 3.4):
 *   - matured cohorts only in the headline: "7 days" = people engaged in [asOf - W - 7 d, asOf - W), labelled with those dates; the
 *     touches of the last W days are "still maturing" - their own tile and the dashed tail of the trend, never mixed into the headline;
 *   - like-for-like change: the previous period of the same length, also matured, in pp;
 *   - a rate is null (shown "-", never 0%) when nobody was engaged, or when no follow-back read covers the period (no read of the
 *     list at or after its first day);
 *   - minimum n (minRankN, 30) to rank a target, a source type or a slot.
 */

export const FB_TAG = '[R16M-FOLLOWBACK]';
export const DEFAULT_WINDOW_DAYS = 7;
export const DEFAULT_MIN_RANK_N = 30;
const DAY_MS = 86400000;

/** The page's ranges (group page: 7 / 30 / All; the slot card adds the Slot Stats page's 90). */
export const PAGE_RANGES = [7, 30, 'all'];

/*
 * Colour follows the source type (D12, stats.css --viz-src-N): post likers violet, post commenters orange, followers lists teal - the
 * hues of Likes / Comments / Follows on the Actions chart, a deliberate echo. Every other source type folds into "Other" in the charts
 * (the de-emphasis grey) and stays in full in the tables. "All sources" is the light neutral context line.
 */
export const CHART_SOURCES = ['LIKERS', 'COMMENTERS', 'FOLLOWERS'];
export const OTHER_KEY = 'OTHER';
export const ALL_KEY = 'ALL';
const SOURCE_COLOR = {
  LIKERS: 'var(--viz-src-1)',
  COMMENTERS: 'var(--viz-src-2)',
  FOLLOWERS: 'var(--viz-src-3)',
  [OTHER_KEY]: 'var(--viz-deemph)',
  [ALL_KEY]: 'var(--viz-src-all)',
};
export const sourceColor = (key) => SOURCE_COLOR[key] || SOURCE_COLOR[OTHER_KEY];
/** The chart key a source type is drawn under: itself, or "Other". */
export const chartKeyOf = (key) => (CHART_SOURCES.includes(key) ? key : OTHER_KEY);

/** Used only when the server sends no label for a key (the labels are the server's, design 3.6). */
const FALLBACK_LABELS = {
  LIKERS: 'Post likers',
  COMMENTERS: 'Post commenters',
  FOLLOWERS: 'Followers lists',
  FOLLOWINGS: 'Followings lists',
  POSTERS: 'Posters',
  DIRECT: 'Direct accounts',
  STORYMENTION: 'Story mentions',
  HIGHLIGHTSHARE: 'Highlight shares',
  HE_POSTOWNER: 'Post owners',
  UNKNOWN: 'Unknown source',
  [OTHER_KEY]: 'Other sources',
  [ALL_KEY]: 'All sources',
};

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const num = (v) => {
  if (isNum(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};
const count = (v) => {
  const n = num(v);
  return n === null ? 0 : Math.max(0, Math.round(n));
};
const nullableCount = (v) => (v === null || v === undefined ? null : count(v));
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const arr = (v) => (Array.isArray(v) ? v : []);

/* ------------------------------------------------------------------ time */

/** Epoch ms from a number (ms; seconds when < 1e11), an ISO date / date-time string, or null. */
export function toMs(v) {
  if (v === null || v === undefined || v === '') return null;
  if (isNum(v)) return v < 1e11 ? v * 1000 : v;
  const s = String(v).trim();
  if (/^\d+$/.test(s)) return toMs(Number(s));
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

/**
 * The scope's UTC offset in minutes: a number of minutes, "+05:30" / "UTC+5:30" / "GMT-04" / "Z", or an IANA name ("Asia/Kolkata",
 * read through Intl at `atMs`). Anything else: 0 (UTC).
 */
export function tzOffsetMinutes(tz, atMs = Date.now()) {
  if (isNum(tz)) return Math.abs(tz) <= 16 ? tz * 60 : tz;   // hours or minutes
  if (typeof tz !== 'string' || !tz.trim()) return 0;
  const s = tz.trim();
  if (/^(z|utc|gmt)$/i.test(s)) return 0;
  const m = s.match(/^(?:utc|gmt)?\s*([+-])(\d{1,2})(?::?(\d{2}))?$/i);
  if (m) return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0));
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: s, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date(atMs));
    const g = (t) => Number((parts.find((p) => p.type === t) || {}).value);
    const local = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'));
    return Math.round((local - Math.floor(atMs / 60000) * 60000) / 60000);
  } catch (e) {
    return 0;
  }
}

/** "UTC+5:30" for an offset in minutes. */
export function tzLabel(offsetMin) {
  if (!offsetMin) return 'UTC';
  const a = Math.abs(offsetMin);
  return `UTC${offsetMin < 0 ? '-' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : ''}`;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** The local calendar day ('YYYY-MM-DD') of an instant at a UTC offset. */
export function dayOf(ms, offsetMin = 0) {
  if (!isNum(ms)) return null;
  const d = new Date(ms + offsetMin * 60000);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 'YYYY-MM-DD' plus n days. */
export function addDays(iso, n) {
  const t = toMs(iso);
  return t === null ? null : dayOf(t + n * DAY_MS, 0);
}

/** The Monday of ISO week 'YYYY-Www'. */
function isoWeekMonday(y, w) {
  const jan4 = Date.UTC(y, 0, 4);
  const dow = (new Date(jan4).getUTCDay() + 6) % 7;          // Monday 0
  return dayOf(jan4 - dow * DAY_MS + (w - 1) * 7 * DAY_MS, 0);
}

/** A series bucket as its first local day: 'YYYY-MM-DD' as it is, 'YYYY-Www' as its Monday, epoch ms at the offset. */
export function bucketDay(bucket, offsetMin = 0) {
  if (bucket === null || bucket === undefined) return null;
  const s = String(bucket).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const w = s.match(/^(\d{4})-?W(\d{1,2})$/i);
  if (w) return isoWeekMonday(Number(w[1]), Number(w[2]));
  const t = toMs(bucket);
  return t === null ? null : dayOf(t, offsetMin);
}

/**
 * The matured cohort of a range (design 3.4 rule 6): [asOf - W - N days, asOf - W). `range` 'all' gives { from: null, to }.
 * The previous period of the same length (rule 7): [asOf - W - 2N, asOf - W - N).
 */
export function cohortWindow(asOfMs, windowDays, range) {
  const W = (isNum(windowDays) ? windowDays : DEFAULT_WINDOW_DAYS) * DAY_MS;
  const to = asOfMs - W;
  if (range === 'all' || !isNum(Number(range))) return { from: null, to, prevFrom: null, prevTo: null };
  const N = Number(range) * DAY_MS;
  return { from: to - N, to, prevFrom: to - 2 * N, prevTo: to - N };
}

/* ------------------------------------------------------------------ rates and formats */

/** followed back ÷ engaged, or null (nobody engaged, or the follow-back count is not known). */
export function rateOf(engaged, followedBack) {
  if (!(isNum(engaged) && engaged > 0) || !isNum(followedBack)) return null;
  return followedBack / engaged;
}

/** The change between two rates in percentage points, or null when either is missing. */
export function ppDelta(cur, prev) {
  return isNum(cur) && isNum(prev) ? (cur - prev) * 100 : null;
}

/** 0.0623 -> "6.2%", 0.06 -> "6.0%" (one decimal everywhere, so a column of rates reads alike); under 0.1% two; null -> "-". */
export function fmtRatePct(r, dec = 1) {
  if (!isNum(r)) return '-';
  const p = r * 100;
  const d = p > 0 && p < 0.1 ? 2 : dec;
  return `${p.toFixed(d)}%`;
}

/** +1.32 -> "+1.3 pp", -0.04 -> "0 pp", null -> null. */
export function fmtPp(d) {
  if (!isNum(d)) return null;
  const r = Math.round(d * 10) / 10;
  if (r === 0) return '0.0 pp';
  return `${r > 0 ? '+' : '-'}${Math.abs(r).toFixed(1)} pp`;
}

const fmtN = (n) => (isNum(n) ? Math.round(n).toLocaleString('en-US') : '-');

/**
 * "6.1% · 23 of 377" (the tooltip's and the table's reading of one cell): followed back of the CHECKED people; when some engaged
 * people were on slots whose list was not read after them, "(of 410 engaged)" follows.
 */
export function rateText(engaged, followedBack, checked = engaged, covered = true) {
  const r = covered ? rateOf(checked, followedBack) : null;
  if (!(engaged > 0)) return 'nobody engaged';
  if (r === null) return `- · ${fmtN(engaged)} engaged, not checked`;
  return `${fmtRatePct(r)} · ${fmtN(followedBack)} of ${fmtN(checked)}${isNum(checked) && checked < engaged ? ` (of ${fmtN(engaged)} engaged)` : ''}`;
}

/** A rate's n: its checked people (the denominator), or engaged when the count is not known. */
export function rateN(c) {
  if (!c) return 0;
  return c.checked === null || c.checked === undefined ? (c.engaged || 0) : c.checked;
}

/**
 * "23 of 377" for a set of counts with its rate: followed back of the CHECKED people, and how many more were engaged on slots whose
 * list was not read after them ("23 of 377, 33 more not checked"); "410 engaged, not checked" without a rate; "nobody engaged".
 */
export function ofText(c) {
  if (!c || !(c.engaged > 0)) return 'nobody engaged';
  if (c.rate === null || c.rate === undefined || !isNum(c.checked)) return `${fmtN(c.engaged)} engaged, not checked`;
  const more = c.engaged - c.checked;
  return `${fmtN(c.followedBack)} of ${fmtN(c.checked)}${more > 0 ? `, ${fmtN(more)} more not checked` : ''}`;
}

/* ------------------------------------------------------------------ the answer */

/** The rate's denominator of a set of counts: the server's engagedChecked, else (an older server) engaged when followedBack is known. */
function checkedOf(o, engaged, followedBack) {
  if (Object.prototype.hasOwnProperty.call(o, 'engagedChecked')) return nullableCount(o.engagedChecked);
  return followedBack === null ? null : engaged;
}

function counts(v) {
  const o = obj(v) || {};
  const engaged = count(o.engaged);
  const followedBack = nullableCount(o.followedBack);
  return { engaged, followedBack, checked: checkedOf(o, engaged, followedBack) };
}

/** a + b for nullable counts: null as soon as either is not known. */
const addN = (a, b) => (a === null || b === null || a === undefined || b === undefined ? null : a + b);

/** A slot reference ('slot801', 801 or '801') as the slot's name. */
export function slotName(s) {
  if (s === null || s === undefined) return null;
  const t = String(s).trim();
  if (/^\d+$/.test(t)) return `slot${t}`;
  return t || null;
}

/** The slot's number (slot801 -> 801), or null. */
export function slotNumber(s) {
  const m = String(s === null || s === undefined ? '' : s).match(/(\d+)\s*$/);
  return m ? Number(m[1]) : null;
}

/**
 * The server's answer, normalised (null when it is not a follow-back answer at all). Counts that are missing read 0, except a
 * follow-back count the server sends as null (not known), which stays null.
 */
export function normaliseFollowBack(raw) {
  const a = obj(raw);
  if (!a || !(obj(a.totals) || Array.isArray(a.series) || Array.isArray(a.bySourceType))) return null;
  const scope = obj(a.scope) || {};
  const asOf = toMs(a.asOf) || Date.now();
  const tz = isNum(scope.tzOffsetMinutes) ? scope.tzOffsetMinutes : tzOffsetMinutes(scope.tz, asOf);
  const windowDays = num(obj(a.window) && a.window.days) || DEFAULT_WINDOW_DAYS;
  const minRankN = num(a.minRankN) || DEFAULT_MIN_RANK_N;
  const mat = obj(a.maturing) || {};
  const matRange = obj(a.maturingRange) || mat;
  const matCounts = obj(a.maturingCounts) || mat;
  const cohort = obj(a.cohort) || {};
  const prev = obj(a.previous);
  const signal = obj(a.signal) || {};
  const methods = obj(signal.methods) || {};

  const labels = {};
  arr(a.sourceTypes).forEach((s) => {
    const o = obj(s);
    if (o && o.key) labels[String(o.key)] = o.label ? String(o.label) : null;
  });
  const labelOf = (key) => labels[key] || FALLBACK_LABELS[key] || String(key || 'Unknown source');

  const slots = arr(scope.slots).map((s) => {
    const o = obj(s) || {};
    return {
      slot: slotName(o.slot), device: o.device ? String(o.device) : null,
      trackingSince: toMs(o.trackingSince), baselineNames: nullableCount(o.baselineNames),
    };
  }).filter((s) => s.slot);
  const since = slots.map((s) => s.trackingSince).filter(isNum);
  const batch = obj(a.batch) || obj(scope.batch);
  const batchSize = batch ? (batch.slotCount !== undefined && batch.slotCount !== null ? batch.slotCount
    : (Array.isArray(batch.members) ? batch.members.length : batch.members)) : null;

  const totals = obj(a.totals) || {};
  const model = {
    kind: scope.kind ? String(scope.kind) : (slots.length > 1 ? 'slots' : 'slot'),
    id: scope.id !== undefined && scope.id !== null ? String(scope.id) : null,
    label: scope.label ? String(scope.label) : null,
    slots,
    batch: batch && batch.id !== undefined && batch.id !== null
      ? { id: String(batch.id), label: batch.label ? String(batch.label) : `Batch ${batch.id}`, slotCount: nullableCount(batchSize) }
      : null,
    tz,
    tzLabel: tzLabel(tz),
    asOf,
    windowDays,
    minRankN,
    cohort: { from: toMs(cohort.from), to: toMs(cohort.to) },
    maturingRange: { from: toMs(matRange.from), to: toMs(matRange.to) },
    maturing: counts(matCounts),
    previous: prev ? {
      cohort: { from: toMs(obj(prev.cohort) && prev.cohort.from), to: toMs(obj(prev.cohort) && prev.cohort.to) },
      ...counts(prev),
    } : null,
    signal: {
      slotsReadOk24h: nullableCount(signal.slotsReadOk24h),
      slotsTotal: nullableCount(signal.slotsTotal),
      lastReadAt: toMs(signal.lastReadAt),
      methods: { OWNLIST: count(methods.OWNLIST), NOTIF: count(methods.NOTIF) },
      note: signal.note ? String(signal.note) : null,
      notReporting: Array.isArray(signal.notReporting) ? signal.notReporting.map(slotName).filter(Boolean) : null,
    },
    labelOf,
    sourceKeys: Object.keys(labels),
    // the server decides coverage per slot (it sends engagedChecked): a count it does not cover arrives as null
    serverCoverage: Object.prototype.hasOwnProperty.call(totals, 'engagedChecked'),
    totals: {
      ...counts(totals),
      ambiguous: count(totals.ambiguous),
      late: count(totals.late),
      preFollowersExcluded: count(totals.preFollowersExcluded),
      beforeTracking: count(totals.beforeTracking),
      unknownOutcome: count(totals.unknownOutcome),
    },
    bySourceType: arr(a.bySourceType).map((s) => {
      const o = obj(s) || {};
      return { key: String(o.key || 'UNKNOWN'), ...counts(o), maturing: counts(o.maturing) };
    }),
    byTarget: arr(a.byTarget).map((t) => {
      const o = obj(t) || {};
      return {
        target: o.target === null || o.target === undefined ? '' : String(o.target),
        sourceType: String(o.sourceType || 'UNKNOWN'),
        ...counts(o), lastEngagedAt: toMs(o.lastEngagedAt),
      };
    }).filter((t) => t.target !== ''),
    series: arr(a.series).map((b) => {
      const o = obj(b) || {};
      const bySource = {};
      Object.entries(obj(o.bySource) || {}).forEach(([k, v]) => { bySource[k] = counts(v); });
      return {
        day: bucketDay(o.bucket, tz),
        granularity: o.granularity === 'week' || /W/i.test(String(o.bucket || '')) ? 'week' : 'day',
        maturing: o.maturing === true,
        bySource,
      };
    }).filter((b) => b.day).sort((x, y) => (x.day < y.day ? -1 : x.day > y.day ? 1 : 0)),
    bySlot: arr(a.bySlot).map((s) => {
      const o = obj(s) || {};
      const bySource = {};
      Object.entries(obj(o.bySource) || {}).forEach(([k, v]) => { bySource[k] = counts(v); });
      const p = obj(o.previous);
      return {
        slot: slotName(o.slot), device: o.device ? String(o.device) : null,
        ...counts(o), bySource,
        signalOk: o.signalOk === true, trackingSince: toMs(o.trackingSince),
        previous: p ? counts(p) : null,
      };
    }).filter((s) => s.slot),
    trackingSince: since.length ? Math.min(...since) : null,
    attributionSince: toMs(scope.attributionSince || a.attributionSince),
    // the server echoes the target filter it applied (null = every target)
    target: a.target === null || a.target === undefined || String(a.target).trim() === '' ? null : String(a.target),
  };
  // the source types the answer names, in the fixed chart order first, then the rest as the server listed them
  const named = new Set([...model.sourceKeys, ...model.bySourceType.map((s) => s.key)]);
  model.sourceOrder = [...CHART_SOURCES.filter((k) => named.has(k)), ...[...named].filter((k) => !CHART_SOURCES.includes(k))];
  return model;
}

/** A local day ('YYYY-MM-DD') of a server instant in the scope's offset. */
export const localDay = (m, ms) => dayOf(ms, m.tz);

/** Some read of the follow-back list happened at or after `fromMs` (null `fromMs` = any read at all). */
export function covered(m, fromMs) {
  // a server that sends engagedChecked has decided coverage per slot already (null counts): nothing more to gate here
  if (m.serverCoverage) return true;
  const last = m.signal.lastReadAt;
  if (!isNum(last)) return false;
  return !isNum(fromMs) || last >= fromMs;
}

/** The cohort's days, labelled: { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } (to = the last day it touches). */
export function cohortDays(m, c = m.cohort) {
  if (!c || !isNum(c.to)) return null;
  const from = isNum(c.from) ? localDay(m, c.from) : (isNum(m.trackingSince) ? localDay(m, m.trackingSince) : null);
  return { from, to: localDay(m, c.to - 1) };
}

/**
 * Something was tracked before the selected cohort: people engaged in the previous period, or the scope's follow-back tracking (or
 * attribution) started more than a day before the cohort. Then an empty cohort is a quiet period, not a new slot.
 */
export function hasEarlierData(m) {
  if (m.previous && m.previous.engaged > 0) return true;
  const from = m.cohort.from;
  if (!isNum(from)) return false;
  const since = [m.attributionSince, m.trackingSince].filter(isNum);
  return since.some((s) => s < from - DAY_MS);
}

/**
 * The headline (design 3.7 KPI row): the matured cohort's rate with its dates, the like-for-like change in pp, and the still-maturing
 * figure. The headline is NEVER the maturing figure. `state`:
 *   'ok'          the cohort has people and a read covers it;
 *   'noSignal'    engaged, no read covers it (rate "-", never 0%);
 *   'notMatured'  only the last W days hold people, and nothing was tracked before (a new slot);
 *   'emptyCohort' nobody in the cohort, people in the last W days, and earlier data exists (a quiet period, or a target filter);
 *   'idle'        nobody in the cohort or since, and earlier data exists (or a target filter);
 *   'noTouches'   nothing attributed at all (a new slot).
 * `figures`: the KPI row and the charts are drawn (ok / noSignal / notMatured / emptyCohort).
 */
export function headline(m) {
  const t = m.totals;
  const cov = covered(m, m.cohort.from) && t.followedBack !== null;
  const rate = cov ? rateOf(t.checked, t.followedBack) : null;
  const prev = m.previous;
  const prevCov = prev ? covered(m, prev.cohort.from) : false;
  const prevRate = prev && prevCov ? rateOf(prev.checked, prev.followedBack) : null;
  const matCov = covered(m, m.maturingRange.from);
  const maturingRate = matCov ? rateOf(m.maturing.checked, m.maturing.followedBack) : null;
  const earlier = hasEarlierData(m) || !!m.target;
  let state = 'ok';
  if (t.engaged === 0 && m.maturing.engaged === 0) state = earlier ? 'idle' : 'noTouches';
  else if (t.engaged === 0) state = earlier ? 'emptyCohort' : 'notMatured';
  else if (!cov) state = 'noSignal';
  // why there is no like-for-like change (the hero says it in words)
  let prevWhy = null;
  if (prev && prevRate === null) {
    if (prev.engaged > 0) prevWhy = 'notCovered';
    else if (isNum(m.trackingSince) && isNum(prev.cohort.from) && m.trackingSince > prev.cohort.from) prevWhy = 'beforeTracking';
    else prevWhy = 'nobody';
  }
  return {
    state,
    figures: state !== 'noTouches' && state !== 'idle',
    target: m.target || null,
    rate,
    engaged: t.engaged,
    checked: t.checked,
    followedBack: t.followedBack,
    covered: cov,
    days: cohortDays(m),
    previous: prev ? {
      rate: prevRate, engaged: prev.engaged, checked: prev.checked, followedBack: prev.followedBack, days: cohortDays(m, prev.cohort), why: prevWhy,
    } : null,
    trackingSinceDay: isNum(m.trackingSince) ? localDay(m, m.trackingSince) : null,
    deltaPp: ppDelta(rate, prevRate),
    maturing: {
      rate: maturingRate,
      engaged: m.maturing.engaged,
      checked: m.maturing.checked,
      followedBack: m.maturing.followedBack,
      covered: matCov,
      days: isNum(m.maturingRange.from) ? { from: localDay(m, m.maturingRange.from), to: localDay(m, (m.maturingRange.to || m.asOf) - 1) } : null,
    },
    firstMatures: firstMatureDay(m),
  };
}

/** When nothing has matured: the day the first engagement matures (its day + W), from the series; null when unknown. */
export function firstMatureDay(m) {
  const first = m.series.find((b) => Object.values(b.bySource).some((c) => c.engaged > 0));
  return first ? addDays(first.day, m.windowDays) : null;
}

/** The source types of the cohort, each with its rate and n, in the fixed order (tables keep every type; `chart` folds to Other). */
export function sourceRows(m, { chart = false } = {}) {
  const cov = covered(m, m.cohort.from);
  const matCov = covered(m, m.maturingRange.from);
  const by = {};
  m.bySourceType.forEach((s) => {
    const k = chart ? chartKeyOf(s.key) : s.key;
    const cur = by[k] || { key: k, engaged: 0, checked: 0, followedBack: 0, fbKnown: true, maturing: { engaged: 0, checked: 0, followedBack: 0, fbKnown: true } };
    cur.engaged += s.engaged;
    if (s.followedBack === null || s.checked === null) cur.fbKnown = false; else { cur.followedBack += s.followedBack; cur.checked += s.checked; }
    cur.maturing.engaged += s.maturing.engaged;
    if (s.maturing.followedBack === null || s.maturing.checked === null) cur.maturing.fbKnown = false;
    else { cur.maturing.followedBack += s.maturing.followedBack; cur.maturing.checked += s.maturing.checked; }
    by[k] = cur;
  });
  const order = chart
    ? [...CHART_SOURCES, OTHER_KEY]
    : [...m.sourceOrder, ...Object.keys(by).filter((k) => !m.sourceOrder.includes(k))];
  return order.filter((k) => by[k]).map((k) => {
    const s = by[k];
    const fb = s.fbKnown ? s.followedBack : null;
    const ck = s.fbKnown ? s.checked : null;
    const mfb = s.maturing.fbKnown ? s.maturing.followedBack : null;
    const mck = s.maturing.fbKnown ? s.maturing.checked : null;
    return {
      key: k,
      label: k === OTHER_KEY ? FALLBACK_LABELS[OTHER_KEY] : m.labelOf(k),
      color: sourceColor(chart ? k : chartKeyOf(k)),
      engaged: s.engaged,
      checked: ck,
      followedBack: fb,
      rate: cov ? rateOf(ck, fb) : null,
      rankable: (ck === null ? s.engaged : ck) >= m.minRankN,
      maturing: { engaged: s.maturing.engaged, checked: mck, followedBack: mfb, rate: matCov ? rateOf(mck, mfb) : null },
    };
  });
}

/** The best source type with n >= minRankN (null when none qualifies). `rows`: sourceRows(m). */
export function bestOf(rows) {
  const ranked = rows.filter((r) => r.rankable && r.rate !== null);
  if (!ranked.length) return null;
  return ranked.reduce((b, r) => (r.rate > b.rate || (r.rate === b.rate && r.engaged > b.engaged) ? r : b));
}

/**
 * The target ranking (design 3.7 chart 5): one row per target with a bar per (folded) source type, sorted by rate; targets under
 * minRankN are "too few to rank" and sorted last (by n). `average`: the scope's rate over every target.
 */
export function targetRanking(m) {
  const cov = covered(m, m.cohort.from);
  const by = new Map();
  m.byTarget.forEach((t) => {
    const cur = by.get(t.target) || { target: t.target, engaged: 0, checked: 0, followedBack: 0, fbKnown: true, lastEngagedAt: null, parts: {}, types: {} };
    const known = t.followedBack !== null && t.checked !== null;
    cur.engaged += t.engaged;
    if (!known) cur.fbKnown = false; else { cur.followedBack += t.followedBack; cur.checked += t.checked; }
    if (isNum(t.lastEngagedAt) && (cur.lastEngagedAt === null || t.lastEngagedAt > cur.lastEngagedAt)) cur.lastEngagedAt = t.lastEngagedAt;
    const k = chartKeyOf(t.sourceType);
    const p = cur.parts[k] || { engaged: 0, checked: 0, followedBack: 0, fbKnown: true };
    p.engaged += t.engaged;
    if (!known) p.fbKnown = false; else { p.followedBack += t.followedBack; p.checked += t.checked; }
    cur.parts[k] = p;
    const ty = cur.types[t.sourceType] || { engaged: 0, checked: 0, followedBack: 0, fbKnown: true };
    ty.engaged += t.engaged;
    if (!known) ty.fbKnown = false; else { ty.followedBack += t.followedBack; ty.checked += t.checked; }
    cur.types[t.sourceType] = ty;
    by.set(t.target, cur);
  });
  const rows = [...by.values()].map((r) => {
    const fb = r.fbKnown ? r.followedBack : null;
    const ck = r.fbKnown ? r.checked : null;
    const nOf = (c, e) => (c === null ? e : c);                 // the rate's n: the checked people (engaged when not known)
    const parts = {};
    Object.entries(r.parts).forEach(([k, p]) => {
      const pfb = p.fbKnown ? p.followedBack : null;
      const pck = p.fbKnown ? p.checked : null;
      parts[k] = {
        engaged: p.engaged, checked: pck, followedBack: pfb, rate: cov ? rateOf(pck, pfb) : null, rankable: nOf(pck, p.engaged) >= m.minRankN,
      };
    });
    const types = Object.entries(r.types).map(([k, p]) => {
      const pfb = p.fbKnown ? p.followedBack : null;
      const pck = p.fbKnown ? p.checked : null;
      return { key: k, label: m.labelOf(k), engaged: p.engaged, checked: pck, followedBack: pfb, rate: cov ? rateOf(pck, pfb) : null };
    });
    return {
      target: r.target, engaged: r.engaged, checked: ck, followedBack: fb, rate: cov ? rateOf(ck, fb) : null,
      rankable: nOf(ck, r.engaged) >= m.minRankN, lastEngagedAt: r.lastEngagedAt, parts, types,
    };
  });
  rows.sort((a, b) => {
    if (a.rankable !== b.rankable) return a.rankable ? -1 : 1;
    if (a.rankable && a.rate !== b.rate) return (b.rate === null ? -1 : b.rate) - (a.rate === null ? -1 : a.rate);
    if (a.engaged !== b.engaged) return b.engaged - a.engaged;
    return a.target.localeCompare(b.target);
  });
  const known = rows.every((r) => r.followedBack !== null && r.checked !== null);
  const sumE = known ? rows.reduce((s, r) => s + r.checked, 0) : null;
  const sumF = known ? rows.reduce((s, r) => s + r.followedBack, 0) : null;
  const keys = [...CHART_SOURCES, OTHER_KEY].filter((k) => rows.some((r) => r.parts[k] && r.parts[k].engaged > 0));
  return { rows, keys, average: cov ? rateOf(sumE, sumF) : null, best: rows.find((r) => r.rankable && r.rate !== null) || null };
}

/**
 * Daily buckets are grouped into 7-day groups when a day holds too few people to read alone: the median day (all sources, matured)
 * under DAY_MIN_PEOPLE. A single slot engages a few dozen people a day per source, so its daily rates would swing 0% - 30% on noise.
 */
export const DAY_MIN_PEOPLE = 150;

const sumParts = (list) => {
  const bySource = {};
  list.forEach((b) => Object.entries(b.bySource).forEach(([k, c]) => {
    const cur = bySource[k] || { engaged: 0, checked: 0, followedBack: 0 };
    cur.engaged += c.engaged;
    cur.followedBack = addN(cur.followedBack, c.followedBack);
    cur.checked = addN(cur.checked, c.checked);
    bySource[k] = cur;
  }));
  return bySource;
};
const dayTotal = (b) => Object.values(b.bySource).reduce((a, c) => a + c.engaged, 0);

/** "12:00" - the local clock time of an instant at the scope's offset. */
export function localClock(m, ms) {
  return isNum(ms) ? new Date(ms + m.tz * 60000).toISOString().slice(11, 16) : null;
}

/**
 * Each server bucket with the part of it the answer really covers: [max(bucket start, cohort start), min(bucket end, asOf)). The
 * first bucket of a range starts at the cohort's start (asOf - W - N: 12:00 when the page is read at noon), and the last one is today
 * so far; for All time the first week starts when tracking started, the last week is this week so far. `partial`: less than the
 * whole day / week. `from` / `to` are the covered local days ('to' is never after asOf).
 */
export function bucketSpans(m) {
  const scopeFrom = isNum(m.cohort.from) ? m.cohort.from : null;
  return m.series.map((b) => {
    const len = b.granularity === 'week' ? 7 : 1;
    const start = toMs(b.day) - m.tz * 60000;
    const end = start + len * DAY_MS;
    const from = scopeFrom !== null ? Math.max(start, Math.min(scopeFrom, end)) : start;
    const to = Math.max(from, Math.min(end, m.asOf));
    const fraction = (to - from) / (end - start);
    return {
      day: b.day,
      from: dayOf(from, m.tz),
      to: dayOf(Math.max(from, to - 1), m.tz),
      fromMs: from,
      toMs: to,
      days: len,
      cover: fraction * len,
      partial: fraction < 0.999,
      startCut: from > start + 60000,
      endCut: to < end - 60000,
      maturing: b.maturing,
      bySource: b.bySource,
      granularity: b.granularity,
    };
  });
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const fmtCover = (d) => (Math.abs(d - Math.round(d)) < 0.05 ? String(Math.round(d)) : d.toFixed(1));

/** Why a part period is in the table only: "from 12:00", "today so far, to 12:00", "this week so far", "1.5 days". */
function partNote(m, s) {
  const today = dayOf(m.asOf, m.tz);
  if (s.days === 1) {
    if (s.endCut) return s.from === today ? `today so far, to ${localClock(m, s.toMs)}` : `so far, to ${localClock(m, s.toMs)}`;
    return `from ${localClock(m, s.fromMs)}`;
  }
  if (s.endCut) return `this week so far, ${fmtCover(s.cover)} days`;
  return `from ${localClock(m, s.fromMs)} on the first day, ${fmtCover(s.cover)} days`;
}

/**
 * The buckets of the trend, oldest first: [{ day, from, to, days, cover, maturing, bySource, grouped, drawn, partial, note, fromMs }].
 *   - the server's buckets (days, or ISO weeks for All), or - `group` 'week', or 'auto' when the median matured day holds fewer than
 *     DAY_MIN_PEOPLE people - 7-day groups counted back from the matured period's end, the maturing days as one more group;
 *   - PART periods are never drawn as if they were whole (review 2026-10-20): the cut first bucket (the cohort starts at 12:00) and
 *     the running last one (today so far, this week so far) are `drawn: false`; a 7-day group that is short or holds a cut day is a
 *     stub, `drawn: false`. They stay in the table twin with a `note`. The tail of the trend therefore ends at the last whole day.
 *     If fewer than two buckets would be drawn, everything is drawn (a very new slot) and the part ones carry `partial` (lighter).
 */
export function trendBuckets(m, group = 'auto') {
  const weeklyServer = m.series.some((b) => b.granularity === 'week');
  const spans = bucketSpans(m).map((s) => ({ ...s, grouped: weeklyServer, drawn: !s.partial, note: s.partial ? partNote(m, s) : null }));
  const finish = (list) => {
    if (list.filter((b) => b.drawn).length >= 2) return list;
    return list.map((b) => ({ ...b, drawn: true }));
  };
  // the running last bucket (today / this week so far) is always its own, undrawn row
  const last = spans.length && spans[spans.length - 1].endCut ? spans[spans.length - 1] : null;
  const body = last ? spans.slice(0, -1) : spans;
  const matured = body.filter((b) => !b.maturing);
  const totals = matured.filter((b) => !b.partial).map(dayTotal).sort((a, b) => a - b);
  const median = totals.length ? totals[Math.floor(totals.length / 2)] : 0;
  const wantGroups = !weeklyServer && group !== 'day' && (group === 'week' || (median < DAY_MIN_PEOPLE && matured.length >= 8));
  if (!wantGroups) return { buckets: finish(spans), grouped: false };
  const groupOf = (chunk, maturing) => {
    const whole = chunk.length === 7 && chunk.every((b) => !b.partial);
    const cover = chunk.reduce((a, b) => a + b.cover, 0);
    return {
      day: chunk[0].day, from: chunk[0].from, to: chunk[chunk.length - 1].to, fromMs: chunk[0].fromMs, toMs: chunk[chunk.length - 1].toMs,
      days: chunk.length, cover, maturing, bySource: sumParts(chunk), grouped: true,
      drawn: maturing ? chunk.every((b) => !b.partial) : whole,
      partial: !whole,
      note: whole ? null : `${fmtCover(cover)} ${cover === 1 ? 'day' : 'days'}, not a whole week`,
    };
  };
  const out = [];
  for (let end = matured.length; end > 0; end -= 7) out.unshift(groupOf(matured.slice(Math.max(0, end - 7), end), false));
  const mat = body.filter((b) => b.maturing);
  if (mat.length) out.push(groupOf(mat, true));
  if (last) out.push({ ...last, grouped: true });
  return { buckets: finish(out), grouped: true };
}

/**
 * The trend rows (design 3.7 chart 4), oldest first: per bucket the rate of each charted source type and of all sources, split into
 * the matured line (`r_<KEY>`) and the dashed maturing tail (`m_<KEY>`, which starts at the last matured point so the line joins);
 * `parts` (engaged per key) feed the stacked "people engaged" bars, `partial` = still maturing or a part period (the lighter fill).
 * A point is drawn only when its bucket holds at least minRankN people of that source (fewer read as noise; the tooltip and the
 * table still give them). `group`: 'auto' | 'day' | 'week' (trendBuckets).
 * Returns { rows: the DRAWN buckets (both charts), tableRows: every bucket (the table twin, part periods with their note), keys,
 * lineKeys: the keys drawn as a line, sparse: the keys with too few points for a line (fewer than half their buckets reach n), weekly,
 * grouped, parts: the undrawn part periods }.
 */
export function trendRows(m, group = 'auto') {
  const keys = [...CHART_SOURCES, OTHER_KEY].filter((k) => m.series.some((b) => Object.entries(b.bySource)
    .some(([sk, c]) => chartKeyOf(sk) === k && c.engaged > 0)));
  const { buckets, grouped } = trendBuckets(m, group);
  const weekly = grouped || m.series.some((b) => b.granularity === 'week');
  const tableRows = buckets.map((b) => {
    const cov = covered(m, b.fromMs);
    const by = {};
    let allE = 0; let allC = 0; let allF = 0; let allKnown = true;
    Object.entries(b.bySource).forEach(([sk, c]) => {
      const k = chartKeyOf(sk);
      const cur = by[k] || { engaged: 0, checked: 0, followedBack: 0, fbKnown: true };
      cur.engaged += c.engaged;
      allE += c.engaged;
      if (c.followedBack === null || c.checked === null) { cur.fbKnown = false; allKnown = false; } else {
        cur.followedBack += c.followedBack; cur.checked += c.checked; allF += c.followedBack; allC += c.checked;
      }
      by[k] = cur;
    });
    // a point's n is its CHECKED people (the rate's denominator); engaged when the count is not known
    const cell = (e, c, f, known) => {
      const ck = known ? c : null;
      const fb = known ? f : null;
      return { engaged: e, checked: ck, followedBack: fb, rate: cov ? rateOf(ck, fb) : null, plotted: (ck === null ? e : ck) >= m.minRankN };
    };
    const cells = {};
    const parts = {};
    keys.forEach((k) => {
      const c = by[k] || { engaged: 0, checked: 0, followedBack: 0, fbKnown: true };
      cells[k] = cell(c.engaged, c.checked, c.followedBack, c.fbKnown);
      parts[k] = c.engaged;
    });
    const all = cell(allE, allC, allF, allKnown);
    return {
      date: b.day, from: b.from, to: b.to, days: b.days, cover: b.cover, maturing: b.maturing, partial: b.maturing || b.partial,
      drawn: b.drawn, note: b.note, covered: cov, cells, all, parts, weekly,
    };
  });
  const rows = tableRows.filter((r) => r.drawn);
  // a source whose points reach n in fewer than half the drawn buckets is not drawn as a line (isolated strokes read as glitches)
  const sparse = keys.filter((k) => rows.length > 0 && rows.filter((r) => r.cells[k].plotted).length < rows.length / 2);
  // the maturing tail joins the matured line at its last point
  const lastMatured = rows.map((r) => !r.maturing).lastIndexOf(true);
  const anyMaturing = rows.some((x) => x.maturing);
  tableRows.forEach((r) => {
    const i = rows.indexOf(r);
    [...keys, ALL_KEY].forEach((k) => {
      const c = k === ALL_KEY ? r.all : r.cells[k];
      const v = i >= 0 && c.plotted ? c.rate : null;
      r[`r_${k}`] = r.maturing ? null : v;
      r[`m_${k}`] = r.maturing || (i === lastMatured && anyMaturing) ? v : null;
    });
  });
  return {
    rows, tableRows, keys, lineKeys: keys.filter((k) => !sparse.includes(k)), sparse, weekly,
    grouped: grouped && !m.series.some((b) => b.granularity === 'week'),
    parts: tableRows.filter((r) => !r.drawn),
  };
}

/**
 * The hero's sparkline: the all-sources rate of the matured period by week - the trend's weekly rows, or its matured days summed in
 * 7-day groups from the end (with fewer than 14 days, by day). In %.
 */
export function heroSpark(trend) {
  const rows = trend.rows.filter((r) => !r.maturing);
  const val = (e, f) => (e > 0 && f !== null && f !== undefined ? (f / e) * 100 : null);
  if (trend.weekly || rows.length < 14) return rows.map((r) => (r.covered ? val(r.all.checked, r.all.followedBack) : null)).filter((v) => v !== null);
  const out = [];
  for (let end = rows.length; end - 7 >= 0; end -= 7) {
    const chunk = rows.slice(end - 7, end);
    const unknown = chunk.some((r) => r.all.followedBack === null || r.all.checked === null || !r.covered);
    const e = unknown ? 0 : chunk.reduce((a, r) => a + r.all.checked, 0);
    const f = unknown ? null : chunk.reduce((a, r) => a + r.all.followedBack, 0);
    out.unshift(val(e, f));
  }
  return out.filter((v) => v !== null);
}

/** The per-slot table (group scope, design 3.7 item 6): rate, rate per source type, pp change when the server sends a previous. */
export function slotRows(m) {
  const cov = covered(m, m.cohort.from);
  return m.bySlot.map((s) => {
    const bySource = {};
    Object.entries(s.bySource).forEach(([k, c]) => { bySource[k] = { ...c, rate: cov ? rateOf(c.checked, c.followedBack) : null }; });
    // a slot not read OK in the last 24 h keeps its rate (earlier reads may cover the cohort); the table marks it "not reporting"
    const rate = cov ? rateOf(s.checked, s.followedBack) : null;
    const prevRate = s.previous && cov ? rateOf(s.previous.checked, s.previous.followedBack) : null;
    return {
      key: s.slot, slot: s.slot, number: slotNumber(s.slot), device: s.device, trackingSince: s.trackingSince,
      engaged: s.engaged, checked: s.checked, followedBack: s.followedBack, rate, rankable: (s.checked === null ? s.engaged : s.checked) >= m.minRankN,
      signalOk: s.signalOk,
      bySource, deltaPp: ppDelta(rate, prevRate),
    };
  });
}

/** Sort the slot table: by `key` ('slot' numeric, or a figure), direction 'asc' | 'desc'; nulls always last. */
export function sortSlotRows(rows, key = 'slot', dir = 'asc') {
  const val = (r) => {
    if (key === 'slot') return r.number !== null ? r.number : r.slot;
    if (key.startsWith('src:')) { const c = r.bySource[key.slice(4)]; return c ? c.rate : null; }
    return r[key];
  };
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = val(a); const y = val(b);
    if (x === null || x === undefined) return (y === null || y === undefined) ? 0 : 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'string' || typeof y === 'string') return sign * String(x).localeCompare(String(y));
    return sign * (x - y) || String(a.slot).localeCompare(String(b.slot));
  });
}

/** Slots of the group whose follow-back list was not read OK in the last 24 h (listed by slot number in the signal strip). */
export function notReporting(m) {
  if (m.signal.notReporting) return m.signal.notReporting;
  return m.bySlot.filter((s) => !s.signalOk).map((s) => s.slot);
}

/*
 * What a failed r16m call means, from the server's own answer: { error: <code>, message } (FollowBackUiController.problem). A 404 / 405
 * WITHOUT a code of the form snake_case is a server with no such route (Spring's own body says error: "Not Found").
 */
export function classifyFbError(err) {
  const res = err && err.response;
  const status = res && typeof res.status === 'number' ? res.status : null;
  const data = res && res.data && typeof res.data === 'object' ? res.data : null;
  const code = data && typeof data.error === 'string' && /^[a-z][a-z0-9_]*$/.test(data.error) ? data.error : null;
  const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const message = data ? (text(data.message) || text(data.detail)) : null;
  let kind = 'other';
  if (code === 'not_enabled') kind = 'notEnabled';
  else if (code === 'slot_not_found') kind = 'slotNotFound';
  else if (code === 'batch_not_found' || code === 'not_found') kind = 'notFound';
  else if (code) kind = 'refused';
  else if (status === 404 || status === 405) kind = 'routeMissing';
  return { kind, status, code, message };
}

/** The signal strip's words (design 3.7 item 2). */
export function signalText(m) {
  const s = m.signal;
  if (!isNum(s.lastReadAt)) {
    return {
      ok: false,
      text: `Follow-backs not checked yet: turn on #92 "Enable Tracking of Follow Backs from each Source?" for ${m.kind === 'slot' ? 'this slot' : 'these slots'}.`,
    };
  }
  if (isNum(s.slotsTotal) && s.slotsTotal > 1) {
    // a group: how many slots were read by each method ("own Followers list 21, notifications 1")
    const parts = [];
    if (s.methods.OWNLIST) parts.push(`own Followers list ${fmtN(s.methods.OWNLIST)}`);
    if (s.methods.NOTIF) parts.push(`notifications ${fmtN(s.methods.NOTIF)}`);
    const how = parts.length ? ` · ${parts.join(', ')}` : '';
    return { ok: s.slotsReadOk24h === s.slotsTotal, text: `Follow-backs checked: ${fmtN(s.slotsReadOk24h)} of ${fmtN(s.slotsTotal)} slots in the last 24 h${how}` };
  }
  // one slot: the method in words (a count of 1 reads like a number of people)
  const how = s.methods.OWNLIST ? ', from its own Followers list' : s.methods.NOTIF ? ', from its notifications' : '';
  const fresh = isNum(s.slotsReadOk24h) ? s.slotsReadOk24h > 0 : (m.asOf - s.lastReadAt) <= DAY_MS;
  return { ok: fresh, text: fresh ? `Follow-backs checked in the last 24 h${how}` : 'Follow-backs not checked in the last 24 h', lastReadAt: s.lastReadAt };
}

/* ------------------------------------------------------------------ scope (the page URL) */

/**
 * The page's scope from its URL: ?batch=<id> or ?slots=<numbers and ranges>, slot NUMBERS only ("801-805,901"). Anything else in
 * slots= is dropped (never sent). Returns { batch } | { slots } | null.
 */
export function scopeFromParams(params) {
  const get = (k) => (params && typeof params.get === 'function' ? params.get(k) : (params || {})[k]);
  const b = get('batch');
  if (b !== null && b !== undefined && /^\d{1,12}$/.test(String(b).trim())) return { batch: String(b).trim() };
  const s = get('slots');
  if (s !== null && s !== undefined) {
    const clean = normaliseSlotList(s);
    if (clean) return { slots: clean };
  }
  return null;
}

/**
 * "slot801-slot805, 901 ;902" -> "801-805,901-902": numbers and ranges only, in order, merged; null when nothing is left. A range is
 * capped at 5,000 slots (the account's size).
 */
export function normaliseSlotList(text) {
  const nums = new Set();
  String(text || '').replace(/[‒–—−]/g, '-').split(/[,;\s]+/).forEach((tok) => {
    const t = tok.trim().replace(/slot/gi, '');
    if (!t) return;
    const r = t.match(/^(\d{1,6})-(\d{1,6})$/);
    if (r) {
      let a = Number(r[1]); let b = Number(r[2]);
      if (a > b) [a, b] = [b, a];
      if (b - a > 5000) b = a + 5000;
      for (let i = a; i <= b; i++) nums.add(i);
    } else if (/^\d{1,6}$/.test(t)) nums.add(Number(t));
  });
  if (!nums.size) return null;
  const sorted = [...nums].sort((x, y) => x - y);
  const out = [];
  let start = sorted[0]; let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const n = sorted[i];
    if (n === prev + 1) { prev = n; continue; }
    out.push(start === prev ? String(start) : `${start}-${prev}`);
    start = n; prev = n;
  }
  return out.join(',');
}

/* ------------------------------------------------------------------ batches (design 2.4 rule 5) */

/**
 * GET /uiapi/follow-back/batches, normalised: [{ id, label, since, retired, devices, members, pending, seeded, onLedger, warnings }].
 * members: [{ slot, device, seeded, onLedger, workerVer }]; pending: [{ slot, device, status: PENDING | DETACHED, why }].
 */
export function normaliseBatches(raw) {
  const list = Array.isArray(raw) ? raw : arr(obj(raw) && raw.batches);
  const asOf = toMs(obj(raw) && raw.asOf) || Date.now();
  const whyOf = (v, status) => {
    const s = String(v || '').toUpperCase();
    if (s.includes('TWO_BATCHES')) return 'TWO_BATCHES';
    if (s.includes('DETACHED') || status === 'DETACHED') return 'DETACHED';
    return 'NOT_MEMBER';
  };
  const pendingOf = (x, fallbackStatus) => {
    const p = obj(x) || {};
    const status = String(p.status || fallbackStatus).toUpperCase();
    return {
      slot: slotName(p.slot), device: p.device ? String(p.device) : null, status,
      why: whyOf(p.why || p.lastAnswer, status), devices: arr(p.devices).map(String),
    };
  };
  return list.map((b) => {
    const o = obj(b) || {};
    const members = arr(o.members).map((x) => {
      const mm = obj(x) || {};
      const last = toMs(mm.lastStateAt);
      return {
        slot: slotName(mm.slot), device: mm.device ? String(mm.device) : null, seeded: mm.seeded === true,
        // on the ledger = the slot's worker made a ledger state call in the last 24 h (the server counts it the same way)
        onLedger: mm.onLedger === true || (isNum(last) && last >= asOf - DAY_MS), lastStateAt: last,
        workerVer: mm.workerVer ? String(mm.workerVer) : null,
      };
    }).filter((x) => x.slot);
    const pending = [
      ...arr(o.pending).map((x) => pendingOf(x, 'PENDING')),
      ...arr(o.detached).map((x) => pendingOf(x, 'DETACHED')),
    ].filter((x) => x.slot);
    const seeded = obj(o.seeded) || {};
    const onLedger = obj(o.onLedger) || {};
    const id = o.id === undefined || o.id === null ? null : String(o.id);
    return {
      id,
      // whether the batch is the logged-in account's: the r16m server sends no owner (an admin's list holds every account's
      // batches), so this is null unless a server says it (`own`, or `mine`)
      own: o.own === true || o.mine === true ? true : (o.own === false || o.mine === false ? false : null),
      label: o.label ? String(o.label) : (id ? `Batch ${id}` : 'Batch'),
      since: toMs(o.since),
      retired: toMs(o.retired),
      devices: arr(o.devices).map(String),
      members,
      pending,
      seeded: {
        n: nullableCount(seeded.n), of: nullableCount(seeded.of),
        missing: arr(seeded.missing).map((x) => (obj(x) ? (x.kind === 'ACCOUNT' ? slotName(x.id) : (x.id ? String(x.id) : null)) : slotName(x)))
          .filter(Boolean),
      },
      onLedger: { n: nullableCount(onLedger.n), of: nullableCount(onLedger.of) },
      warnings: arr(o.warnings).map(String),
    };
  }).filter((b) => b.id);
}

/** The live batch a slot is a member of (null when none). */
export function batchOfSlot(batches, slot) {
  const name = slotName(slot);
  return (batches || []).find((b) => !b.retired && b.members.some((x) => x.slot === name)) || null;
}

/**
 * The live batch where a slot is PENDING or DETACHED (it touches the batch's phones but is not a member, so with #1216 on it
 * engages nobody: CONFLICT, fail closed). { batch, entry } or null.
 */
export function pendingOfSlot(batches, slot) {
  const name = slotName(slot);
  for (const b of batches || []) {
    if (b.retired) continue;
    const entry = b.pending.find((p) => p.slot === name);
    if (entry) return { batch: b, entry };
  }
  return null;
}

/** The pending / detached entries of a live batch (the slots on its phones that engage nobody in batch mode). */
export function stuckSlots(batch) {
  return batch && !batch.retired ? batch.pending : [];
}

/** "Batch 7" - a batch's label without its phone list (for sentences; the panel's header keeps the whole label). */
export function batchName(batch) {
  if (!batch) return 'the batch';
  const l = String(batch.label || '').replace(/:.*$/, '').trim();
  return l || (batch.id ? `Batch ${batch.id}` : 'the batch');
}

/** What a PENDING / DETACHED slot's reason means, in words (design 2.4 rule 3; BatchResolver: a detached slot answers CONFLICT). */
export function pendingReason(p, batch) {
  if (p.why === 'TWO_BATCHES') return `its #1217 phones belong to two batches: in batch mode it engages nobody until #1217 lists only ${batchName(batch)}'s phones`;
  if (p.why === 'DETACHED' || p.status === 'DETACHED') {
    return 'detached from this batch: while its #1216 is on it engages nobody in batch mode (its own phone is a batch phone) until it is joined again, or #1216 is turned off';
  }
  return `not in ${batchName(batch)}: in batch mode it engages nobody until it is joined, or its #1217 is fixed`;
}

/** Short words for a stuck slot's reason (alerts): "not a member" / "detached" / "phones of two batches". */
export function pendingShort(p) {
  if (p.why === 'TWO_BATCHES') return 'phones of two batches';
  if (p.why === 'DETACHED' || p.status === 'DETACHED') return 'detached';
  return 'not a member';
}

/**
 * A long name shortened in the MIDDLE ("brandname_…official"): the start and the end are what tell names of one family apart, so
 * both stay. `max` counts the ellipsis.
 */
export function middleEllipsis(s, max) {
  const t = String(s === null || s === undefined ? '' : s);
  if (!(max > 2) || t.length <= max) return t;
  const keep = max - 1;
  const head = Math.ceil(keep / 2);
  const tail = keep - head;
  return `${t.slice(0, head)}…${t.slice(t.length - tail)}`;
}
